import { useCallback, useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import ParticleField from "./ParticleField";
import Corners from "../wam/Corners";
import YouTubePlayer from "./YouTubePlayer";
import { REPLAY_CONFIG, isReplayActive } from "@/config/replay";
import { attachVideoMilestones } from "@/lib/videoTracking";
import { track } from "@/lib/metaPixel";

// Same hero background image as the main landing page — keeps brand continuity
// across / and /replay.
const HERO_IMAGE =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=2400&q=85&auto=format&fit=crop";

function isYouTube(url) {
  return /youtu\.be|youtube\.com/.test(url);
}
function isVimeo(url) {
  return /vimeo\.com|player\.vimeo\.com/.test(url);
}
function isMp4(url) {
  return /\.mp4($|\?)/i.test(url);
}

function VideoFrame({ url }) {
  const videoRef = useRef(null);
  useEffect(() => {
    if (!videoRef.current) return;
    const clean = attachVideoMilestones(videoRef.current, {
      content_ids: ["webinar_replay_main"],
    });
    return clean;
  }, [url]);

  const onYtMilestone = useCallback((m) => {
    track("ViewContent", {
      content_name: `Replay ${Math.round(m * 100)}%`,
      content_category: "webinar_replay",
      content_ids: ["webinar_replay_main"],
    });
  }, []);

  if (!url) {
    return (
      <div className="flex aspect-video w-full items-center justify-center border border-dashed border-[#C9A84C]/30 bg-white/[0.02] text-center p-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#C9A84C]/70">
            REPLAY VIDEO NOT YET CONFIGURED
          </div>
          <div className="mt-3 font-body text-[13px] text-[#F0ECE4]/50 max-w-[440px]">
            {/* TODO: Set REPLAY_VIDEO_URL in /app/frontend/src/config/replay.js */}
            Once the SBA-approved embed URL is added to the replay config, the
            training will appear here.
          </div>
        </div>
      </div>
    );
  }

  if (isYouTube(url)) {
    return (
      <div className="absolute inset-0" data-testid="replay-video-yt-wrap">
        <YouTubePlayer url={url} onMilestone={onYtMilestone} />
      </div>
    );
  }
  if (isVimeo(url)) {
    return (
      <iframe
        title="Webinar replay"
        src={url}
        className="absolute inset-0 h-full w-full"
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        data-testid="replay-video-iframe"
      />
    );
  }
  if (isMp4(url)) {
    return (
      <video
        ref={videoRef}
        controls
        playsInline
        className="absolute inset-0 h-full w-full object-cover bg-black"
        data-testid="replay-video-native"
      >
        <source src={url} type="video/mp4" />
      </video>
    );
  }
  // Fallback: assume it's an embeddable iframe URL
  return (
    <iframe
      title="Webinar replay"
      src={url}
      className="absolute inset-0 h-full w-full"
      frameBorder="0"
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
      data-testid="replay-video-iframe"
    />
  );
}

export default function ReplayHero() {
  const cfg = REPLAY_CONFIG;
  const active = isReplayActive(cfg);
  const window_ = cfg.REPLAY_WINDOW_OR_EXPIRY_DATE;
  const promise = cfg.WEBINAR_PROMISE;

  // Ken Burns + scroll parallax (matches main site Hero)
  const wrap = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrap,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 40]);
  const dim = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  return (
    <section
      id="watch"
      ref={wrap}
      className="relative overflow-hidden pt-32 md:pt-40 pb-20 md:pb-28 bg-black"
      data-testid="replay-hero"
    >
      {/* Image layer with Ken Burns motion — mirrored from the main landing hero */}
      <motion.div
        aria-hidden
        className="absolute inset-0 z-0"
        style={{ y: imgY, scale: imgScale, opacity: dim }}
      >
        <motion.div
          initial={{ scale: 1, x: 0, y: 0 }}
          animate={{ scale: 1.08, x: -30, y: -20 }}
          transition={{ duration: 22, ease: "linear", repeat: Infinity, repeatType: "reverse" }}
          className="h-full w-full"
        >
          <img
            src={HERO_IMAGE}
            alt=""
            className="h-full w-full object-cover"
            style={{ filter: "brightness(0.62) contrast(1.08) saturate(0.85)" }}
          />
        </motion.div>
      </motion.div>

      {/* Overlays (dark gradient + grid + vignette) — same stack as main hero */}
      <div
        aria-hidden
        className="absolute inset-0 z-[5]"
        style={{
          background:
            "linear-gradient(to bottom, rgba(7,7,9,0.35) 0%, rgba(7,7,9,0.72) 60%, rgba(7,7,9,1) 100%)",
        }}
      />
      <motion.div
        aria-hidden
        className="absolute inset-0 z-[6] wam-grid-bg opacity-40 mix-blend-screen"
        style={{ y: gridY }}
      />
      <div
        aria-hidden
        className="absolute inset-0 z-[7] wam-vignette pointer-events-none"
      />

      {/* Particle background (desktop, non-reduced-motion only) */}
      <div aria-hidden className="absolute inset-0 z-[8] opacity-70 hidden md:block">
        <ParticleField />
      </div>

      {/* Ambient gold glow */}
      <div
        aria-hidden
        className="absolute top-[10%] right-[-10%] h-[600px] w-[600px] z-[9] pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(closest-side, rgba(201,168,76,0.14), transparent 70%)",
        }}
      />

      <div className="relative z-20 mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mb-8 font-mono text-[10px] tracking-[0.3em] text-[#C9A84C]"
          data-testid="replay-eyebrow"
        >
          WEBINAR REPLAY{window_ ? ` · AVAILABLE ${window_.toUpperCase()}` : ""}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 md:gap-16 items-start">
          {/* LEFT — copy */}
          <div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="font-display"
              style={{
                fontSize: "clamp(38px, 5.6vw, 82px)",
                lineHeight: 0.95,
                letterSpacing: "-0.035em",
              }}
            >
              THE SYSTEM IS READY.
              <br />
              <span className="text-[#F0ECE4]/55">NOW MAKE IT</span>
              <br />
              <span className="text-[#C9A84C]">WORK FOR YOU.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="mt-8 max-w-[520px] font-body text-[15px] leading-[1.75] text-[#F0ECE4]"
            >
              {promise ? `${promise} ` : ""}
              Watch the full training, then book a strategy call to map the
              clearest next move for your business.
            </motion.p>

            {/* Video appears on mobile between copy blocks; on lg+ it lives right column */}
            <div className="mt-10 lg:hidden">
              <div className="relative border border-[#C9A84C]/30 bg-black">
                <Corners />
                <div className="relative aspect-video w-full bg-black overflow-hidden">
                  {active ? (
                    <VideoFrame url={cfg.REPLAY_VIDEO_URL} />
                  ) : (
                    <ExpiredState />
                  )}
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-10 flex flex-col items-start gap-4"
            >
              <a
                href="#book"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="wam-btn-primary text-[12px]"
                data-testid="replay-hero-cta"
              >
                Book My Free Strategy Call →
              </a>
              <div className="font-mono text-[10px] tracking-[0.24em] text-[#F0ECE4]/45">
                FOCUSED ON YOUR BUSINESS · CLEAR NEXT STEPS · NO OBLIGATION
              </div>
              {window_ && (
                <div className="font-mono text-[10px] tracking-[0.24em] text-[#F0ECE4]/35">
                  REPLAY ACCESS: {window_.toUpperCase()}
                </div>
              )}
            </motion.div>
          </div>

          {/* RIGHT — video frame (lg+) */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="hidden lg:block"
          >
            <div className="relative border border-[#C9A84C]/30 bg-black shadow-[0_60px_180px_-40px_rgba(0,0,0,0.9)]">
              <Corners />
              <div className="relative aspect-video w-full bg-black overflow-hidden">
                {active ? (
                  <VideoFrame url={cfg.REPLAY_VIDEO_URL} />
                ) : (
                  <ExpiredState />
                )}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-baseline justify-between gap-2 font-mono text-[9px] tracking-[0.28em] text-[#F0ECE4]/35">
              <span>WEBINAR REPLAY · SBA</span>
              {cfg.REPLAY_VIDEO_URL && (
                <a
                  href={cfg.REPLAY_VIDEO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#C9A84C] transition-colors"
                  data-testid="replay-fallback-link"
                >
                  Open video ↗
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ExpiredState() {
  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center text-center px-6"
      data-testid="replay-expired-state"
    >
      <div className="font-mono text-[10px] tracking-[0.3em] text-[#C9A84C]">
        REPLAY WINDOW CLOSED
      </div>
      <div
        className="mt-4 font-display max-w-[420px]"
        style={{ fontSize: "clamp(22px, 2.4vw, 32px)", lineHeight: 1.05, letterSpacing: "-0.02em" }}
      >
        The replay is no longer available.
      </div>
      <p className="mt-3 max-w-[420px] font-body text-[13px] leading-[1.7] text-[#F0ECE4]/55">
        Book a strategy call directly with our team to talk through the ideas
        from the training.
      </p>
      <a
        href="#book"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
        }}
        className="wam-btn-primary mt-6 text-[11px]"
      >
        Book A Strategy Call →
      </a>
    </div>
  );
}
